/**
 * cockpitLoaders — Datenfundament für das Cockpit-Dashboard (M-UX-4.1).
 *
 * Bündelt die in `architecture-ux-restructure.md` §5 spezifizierten
 * Cockpit-Datenquellen als PURE Aggregatoren + dünne Firestore-Loader.
 *
 * DISCIPLINE (Muster `utils/openItemStatus.ts` / `utils/expenseSummary.ts`):
 *   - Aggregator-Funktionen (`summarizeOpenItems`, `sumMonth`,
 *     `sumBookedExpensesMonth`, `deriveChecklist`) sind PURE: kein Firestore-Import,
 *     kein React, keine Seiteneffekte. Trivial unit-testbar ohne Mocking.
 *   - Loader-Funktionen (`load*`) kapseln GENAU einen Firestore-Read-Pfad und
 *     werfen Fehler an den Aufrufer durch (Muster `ExpensesPage.loadIncomeForUser`,
 *     „Fail-closed: kein Page-Crash, Kachel verschwindet" — §5: „Jede Kachel lädt
 *     isoliert (eigener try/catch), Fehler ⇒ Kachel zeigt „—" + Retry").
 *   - Gate-Checks (`canUseInbound`, `canUseBankReconcile`, `canUseReceiptScan`, …)
 *     sind NICHT Teil dieser Loader — das ist Aufgabe der aufrufenden Seite
 *     (CockpitPage, M-UX-4.2), exakt wie beim bestehenden ExpensesPage-Income-Loader.
 *     Bereits vorhandene Cross-User-Guards in den wiederverwendeten APIs
 *     (`getPaymentsForUser`, `getExpensesForUser`, `getScannedReceiptsForUser` werfen
 *     bei `uid !== currentUser.uid`) wirken als zusätzliches Fail-closed-Netz.
 *   - Alle Geldbeträge Integer-Cents. Alle Datumsvergleiche ISO-8601-String-Vergleiche
 *     (lexikografisch == chronologisch für `YYYY-MM-DD`).
 *
 * Kanonische I/O-Paare (Testvertrag, s. architecture-ux-restructure.md §5):
 *   - `sumMonth([...], '2026-07-09')` → nur Einträge im Kalendermonat 2026-07,
 *     Vormonat raus, kaputtes Datum ignoriert (nicht NaN).
 *   - `summarizeOpenItems`: `overdue` zählt in BEIDE Summen (offen ⊇ überfällig).
 *   - `deriveChecklist`: alle Inputs false/leer ⇒ 5 offene Items in fester
 *     Reihenfolge; `profileFieldsComplete===true` ⇒ Item 1 (`profileComplete`) ✓
 *     (der Wizard-Proxy `onboardingCompleted` ist als done-Quelle entkoppelt, §B).
 *
 * @module cockpitLoaders
 */

import {
  collection,
  query,
  where,
  getDocs,
  limit as fsLimit,
  Timestamp,
} from 'firebase/firestore';

import { db } from '../firebaseConfig';
import { eurosToCents } from '../utils/moneyUtils';
import {
  computeOpenItemStatus,
  ComputeOpenItemStatusResult,
} from '../utils/openItemStatus';
import { getPaymentsForUser } from '../api/paymentsApi';
import { Payment } from '../types/paymentTypes';
import type { IncomeEntry } from '../utils/expenseSummary';
import type { Expense } from '../types/expenseTypes';
import { getScannedReceiptsForUser } from '../api/scannedReceiptsApi';
import { listTransactions } from '../api/bankApi';
import { mapInvoiceToVatIncomeEntry, VatInvoiceDoc } from '../utils/vatIncome';
import type { VatIncomeEntry } from '../utils/vatReturn';

// ─── Shared date helpers (bewusst dupliziert, KEIN Import aus pages/ —
//     Muster „Duplikat-Lader akzeptiert", ExpensesPage.tsx:49) ────────────────

/** Today's date as ISO YYYY-MM-DD (local timezone, safe fallback for undated docs). */
function todayFallbackISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** Extracts a YYYY-MM-DD string from a Timestamp, string, or undefined. */
function toDateISO(value: string | Timestamp | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Timestamp) {
    const d = value.toDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;
  }
  return null;
}

/**
 * Validates an ISO date string strictly enough to avoid NaN/garbage comparisons
 * downstream, without a full calendar library.  A malformed `dateISO` is treated
 * as "unknown" and the caller must IGNORE the entry (never coerce to `0`/`NaN`).
 */
function isValidISODate(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return false;
  const month = Number(m[2]);
  const day = Number(m[3]);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

// ─── §5 #1 — Offene Posten (Extrakt OpenItemsPage.tsx:612-685) ────────────────

/** Raw Firestore invoice document shape shared by the loaders below. */
interface CockpitInvoiceDoc {
  id: string;
  userId: string;
  invoiceDate?: string | Timestamp;
  totalAmount?: number;
  isCancelled?: boolean;
  isCancellation?: boolean;
  dueDate?: string | null;
  /** BT-1 Rechnungsnummer (top-level, vgl. UmsatzsteuerPage InvoiceDoc). NEU (M-CFO-0) für den Überfällig-Fokus. */
  invoiceNumber?: string;
  /** BT-44 Käufername (top-level, vgl. UmsatzsteuerPage InvoiceDoc). NEU (M-CFO-0) für den Überfällig-Fokus. */
  buyerName?: string;
  rawData?: {
    invoiceDate?: string;
    dueDate?: string;
    paymentTerms?: { dueDate?: string };
    skonto?: { percentage?: number; days?: number } | null;
  };
}

/** Extracts a due date from an invoice doc (top-level → rawData → rawData.paymentTerms). */
function extractDueDate(inv: CockpitInvoiceDoc): string | null {
  if (inv.dueDate) return inv.dueDate;
  if (inv.rawData?.dueDate) return inv.rawData.dueDate;
  if (inv.rawData?.paymentTerms?.dueDate) return inv.rawData.paymentTerms.dueDate;
  return null;
}

/**
 * Extracts invoiceDate from an invoice doc.
 * Priority: top-level `invoiceDate` (Timestamp or string) → `rawData.invoiceDate` → today.
 */
function extractInvoiceDate(inv: CockpitInvoiceDoc): string {
  const topLevel = toDateISO(inv.invoiceDate);
  if (topLevel) return topLevel;
  if (inv.rawData?.invoiceDate) return inv.rawData.invoiceDate.slice(0, 10);
  return todayFallbackISO();
}

/** Returns the ISO date of the most recent payment, or null. */
function latestPaymentDate(payments: Payment[]): string | null {
  if (payments.length === 0) return null;
  const dates = payments.map((p) => {
    const raw = p.paidAt;
    if (typeof raw === 'string') return raw.slice(0, 10);
    if (raw instanceof Timestamp) return toDateISO(raw) ?? todayFallbackISO();
    return todayFallbackISO();
  });
  return dates.sort().at(-1) ?? null;
}

/** Minimal per-invoice shape consumed by {@link summarizeOpenItems}. */
export interface OpenItemSummaryInput {
  status: ComputeOpenItemStatusResult['status'];
  openAmountCents: number;
}

/** Row returned by {@link loadOpenItemRows} — {@link OpenItemSummaryInput} plus an id. */
export interface CockpitOpenItemRow extends OpenItemSummaryInput {
  invoiceId: string;
  /**
   * NEU (M-F, §5.10): Fälligkeit für die OP-Alterungs-Grafik
   * (`utils/cockpitChartData#ageOpenItems`); `null` wenn unbekannt. Reiner
   * Pass-through des bereits berechneten {@link extractDueDate}-Werts — kein
   * neuer Firestore-Read.
   */
  dueDateISO: string | null;
  /**
   * NEU (M-CFO-0, §7.1): Rechnungsdatum (BT-2) für die DSO-Berechnung
   * (`utils/cockpitReceivables#computeDso`). Reiner Pass-through des im Loader
   * bereits berechneten {@link extractInvoiceDate}-Werts — kein neuer Read.
   * Pflichtfeld (nie `undefined`; der Extraktor liefert immer ein Datum-Fallback).
   */
  invoiceDateISO: string;
  /**
   * NEU (M-CFO-0, §7.1): Datum der jüngsten Zahlung (`latestPaymentDate`) für die
   * DSO-Berechnung; `null` wenn (noch) keine Zahlung erfasst ist. Pflichtfeld mit
   * explizitem `null` (fail-closed: zwingt jeden DSO-Konsumenten zur Behandlung).
   */
  paidDateISO: string | null;
  /**
   * NEU (M-CFO-0, §7.1): Rechnungsnummer für die Überfällig-Fokusliste
   * (`utils/cockpitReceivables#rankOverdueFocus`); `null` wenn nicht gesetzt.
   */
  invoiceNumber: string | null;
  /**
   * NEU (M-CFO-0, §7.1): Käufername für die Überfällig-Fokusliste; `null` wenn
   * nicht gesetzt.
   */
  buyerName: string | null;
}

/**
 * Loads all non-cancelled invoices for `scopeUid`, joins their payments, and
 * computes each invoice's open-item status via `computeOpenItemStatus`.
 *
 * Extrahiert aus `pages/OpenItemsPage.tsx:612-685` (Storno-Filter K2, Skonto-Handling),
 * bewusst OHNE die dort zusätzliche `orderBy('createdAt','desc')` — das Cockpit
 * aggregiert nur, es zeigt keine geordnete Liste, wodurch kein Composite-Index
 * dafür nötig ist.
 *
 * @param scopeUid - `effectiveScopeUid` aus `useAuth()`.
 * @throws Propagates Firestore/`getPaymentsForUser` errors to the caller —
 *   die aufrufende Kachel fängt das ab (§5 Fail-closed-Grundsatz).
 */
export async function loadOpenItemRows(scopeUid: string): Promise<CockpitOpenItemRow[]> {
  const invoicesRef = collection(db, 'invoices');
  const invoicesQuery = query(invoicesRef, where('userId', '==', scopeUid));
  const snap = await getDocs(invoicesQuery);

  const rawInvoices: CockpitInvoiceDoc[] = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<CockpitInvoiceDoc, 'id'>),
  }));

  // Storno-Filter (K2, PFLICHT) — identisch zu OpenItemsPage.
  const activeInvoices = rawInvoices.filter(
    (inv) => !inv.isCancellation && !inv.isCancelled,
  );

  const allPayments = await getPaymentsForUser(scopeUid);
  const paymentsByInvoice = new Map<string, Payment[]>();
  for (const p of allPayments) {
    const arr = paymentsByInvoice.get(p.invoiceId) ?? [];
    arr.push(p);
    paymentsByInvoice.set(p.invoiceId, arr);
  }

  const today = todayFallbackISO();

  return activeInvoices.map((inv) => {
    const invoicePayments = paymentsByInvoice.get(inv.id) ?? [];
    const grandTotalCents = eurosToCents(inv.totalAmount ?? 0);
    const paymentsCents = invoicePayments.map((p) => p.amountCents);

    const invoiceDateISO = extractInvoiceDate(inv);
    const dueDateISO = extractDueDate(inv);
    const paymentDateISO = latestPaymentDate(invoicePayments);

    const rawSkonto = inv.rawData?.skonto;
    const skontoPercentage = rawSkonto?.percentage != null ? rawSkonto.percentage : null;
    const skontoDays = rawSkonto?.days != null ? rawSkonto.days : null;

    const computed = computeOpenItemStatus({
      storedGrandTotalCents: grandTotalCents,
      paymentsCents,
      invoiceDateISO,
      paymentDateISO,
      dueDateISO,
      todayISO: today,
      skontoPercentage,
      skontoDays,
    });

    return {
      invoiceId: inv.id,
      status: computed.status,
      openAmountCents: computed.openAmountCents,
      dueDateISO,
      // M-CFO-0 (§7.1): reiner Pass-through der bereits berechneten Werte —
      // KEIN zusätzlicher Firestore-Read. `?? null` materialisiert nie `undefined`.
      invoiceDateISO,
      paidDateISO: paymentDateISO,
      invoiceNumber: inv.invoiceNumber ?? null,
      buyerName: inv.buyerName ?? null,
    };
  });
}

/** Result of {@link summarizeOpenItems}. All amounts integer cents. */
export interface OpenItemsSummary {
  /** Σ `openAmountCents` aller NICHT `paid` Zeilen (offen ⊇ überfällig). */
  openCents: number;
  /** Anzahl der Zeilen mit `status==='overdue'`. */
  overdueCount: number;
  /** Σ `openAmountCents` der `overdue`-Zeilen (Teilmenge von `openCents`). */
  overdueCents: number;
}

/**
 * Pure aggregation of open-item rows into the cockpit's "Offene Posten"-Kachel
 * figures. `overdue` counts into BOTH `openCents` AND `overdueCents` — "offen"
 * is a superset of "überfällig" (Beschriftungspflicht: „Offen gesamt / davon
 * überfällig", §5 kanonisches I/O-Paar).
 *
 * @param rows - z. B. das Ergebnis von {@link loadOpenItemRows}.
 */
export function summarizeOpenItems(rows: OpenItemSummaryInput[]): OpenItemsSummary {
  let openCents = 0;
  let overdueCount = 0;
  let overdueCents = 0;

  for (const row of rows ?? []) {
    if (!row || row.status === 'paid') continue;

    openCents += row.openAmountCents;

    if (row.status === 'overdue') {
      overdueCount += 1;
      overdueCents += row.openAmountCents;
    }
  }

  return { openCents, overdueCount, overdueCents };
}

// ─── §5 #2 — Umsatz lfd. Monat (Umzug aus ExpensesPage.tsx:103-119) ───────────

/**
 * Loads income (invoices) for the cockpit's revenue figure.
 * Storno-Filter (K2): excludes `isCancellation` / `isCancelled` invoices.
 * Maps each surviving invoice to an `IncomeEntry` (gross cents + invoice date).
 *
 * UMZUG aus `pages/ExpensesPage.tsx:103-119` (M-UX-4.1-Auftrag) — ExpensesPage
 * importiert diese Funktion künftig von hier statt einer eigenen Kopie
 * (kein Verhaltensdelta, identische Implementierung).
 *
 * @throws Propagates Firestore errors to the caller, which sets `income = null`.
 */
export async function loadIncomeForUser(uid: string): Promise<IncomeEntry[]> {
  const invoicesRef = collection(db, 'invoices');
  const invoicesQuery = query(invoicesRef, where('userId', '==', uid));
  const snap = await getDocs(invoicesQuery);

  const rawInvoices: CockpitInvoiceDoc[] = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<CockpitInvoiceDoc, 'id'>),
  }));

  return rawInvoices
    .filter((inv) => !inv.isCancellation && !inv.isCancelled)
    .map((inv) => ({
      dateISO: extractInvoiceDate(inv),
      grossCents: eurosToCents(inv.totalAmount ?? 0),
    }));
}

// ─── M-CFO-0 — USt-Einnahmen-Loader (USt-Rücklage-Kachel, M-CFO-2) ────────────────

/**
 * Lädt ALLE `invoices` des Nutzers und normalisiert sie via des geteilten
 * {@link mapInvoiceToVatIncomeEntry}-Mappers (`utils/vatIncome.ts`) zu
 * {@link VatIncomeEntry}. Basis der USt-Rücklage-Kachel (M-CFO-2), die daraus
 * per `buildVatReturn` die Zahllast des laufenden Quartals ableitet.
 *
 * EIGENER Read (KEINE Erweiterung von {@link loadOpenItemRows}): der Read fällt
 * nur an, wenn die Rücklage-Kachel überhaupt lädt (Gate-Kaskade §8.2), und hält
 * die Kachel-Isolation (eigener Retry) sauber (§7.3).
 *
 * KEIN Storno-Filter: der Mapper negiert Storno-Docs (rateLines × −1), und
 * `buildVatReturn` summiert storno-neutral (§3.7) — ein Herausfiltern würde das
 * Netting brechen. Alle Docs werden bewusst ungefiltert durchgereicht.
 *
 * @param scopeUid - `effectiveScopeUid` aus `useAuth()`.
 * @throws Propagates Firestore errors to the caller — die Rücklage-Kachel fängt
 *   das ab und zeigt ihren isolierten Fehlerzustand (§5 Fail-closed-Grundsatz).
 */
export async function loadVatIncomeForUser(scopeUid: string): Promise<VatIncomeEntry[]> {
  const invoicesRef = collection(db, 'invoices');
  const invoicesQuery = query(invoicesRef, where('userId', '==', scopeUid));
  const snap = await getDocs(invoicesQuery);

  return snap.docs.map((d) =>
    mapInvoiceToVatIncomeEntry({ id: d.id, ...(d.data() as Omit<VatInvoiceDoc, 'id'>) }),
  );
}

// ─── M-CFO-0 — payments-Loader (Ergebnis-YTD cash-Reihe, M-CFO-3) ────────────────

/**
 * Dünner, kachel-isolierter Wrapper um {@link getPaymentsForUser} für die
 * zahlungsbasierte (cash) Reihe der Ergebnis-YTD-Kachel (M-CFO-3, §8.3).
 *
 * Bewusst ein EIGENER Read (D3a Opt. ii) statt eines neuen Bubbles aus
 * {@link loadOpenItemRows}: keine Rückgabetyp-Änderung des OP-Loaders, kein
 * Blast-Radius über die CockpitPage-Verdrahtung. `getPaymentsForUser` ist bereits
 * owner-scoped und wirft bei Cross-User-Zugriff — kein neuer Firestore-Index.
 *
 * @param scopeUid - `effectiveScopeUid`. MUSS `auth.currentUser.uid` entsprechen
 *   (die Rules/`getPaymentsForUser` lehnen Fremd-Reads ab).
 * @throws Propagates auth-/Firestore errors to the caller (Kachel-Isolation).
 */
export async function loadPaymentsForUser(scopeUid: string): Promise<Payment[]> {
  return getPaymentsForUser(scopeUid);
}

/**
 * Pure sum of `grossCents` for all `entries` whose `dateISO` falls in the same
 * calendar month as `todayISO` (Monatsgrenzen inklusive, Vormonat raus).
 *
 * Malformed `dateISO` entries (falsches Format) are IGNORED, never coerced to
 * `NaN`/`0`-poisoning the sum. An entry with `grossCents: 0` legitimately
 * contributes `0`. Empty `entries` → `0`.
 *
 * @param entries  - z. B. das Ergebnis von {@link loadIncomeForUser}.
 * @param todayISO - Referenzdatum `YYYY-MM-DD` (i. d. R. heute), als Parameter
 *                   übergeben, damit die Funktion pure/testbar bleibt.
 */
export function sumMonth(entries: IncomeEntry[], todayISO: string): number {
  if (!isValidISODate(todayISO)) return 0;
  const yearMonth = todayISO.slice(0, 7);

  let totalCents = 0;
  for (const entry of entries ?? []) {
    if (!entry || !isValidISODate(entry.dateISO)) continue;
    if (entry.dateISO.slice(0, 7) !== yearMonth) continue;
    totalCents += entry.grossCents;
  }
  return totalCents;
}

// ─── §5 #3 — Ausgaben lfd. Monat (api/expensesApi.ts + utils/expenseSummary.ts) ─

/**
 * Pure sum of `grossCents` for all `status==='booked'` expenses whose
 * `expenseDate` falls in the same calendar month as `todayISO`.
 *
 * Korrektur-Docs (negierte Koexistenz-Docs, `utils/expenseCorrection.ts`) zählen
 * mit dem Vorzeichen, mit dem sie gespeichert sind — KEIN Herausfiltern, sie
 * sind bereits das korrekte Reduktions-Signal für den Monatssaldo.
 *
 * Malformed `expenseDate` entries are ignored (same discipline as {@link sumMonth}).
 *
 * @param expenses - z. B. das Ergebnis von `api/expensesApi.ts#getExpensesForUser`.
 * @param todayISO - Referenzdatum `YYYY-MM-DD`.
 */
export function sumBookedExpensesMonth(expenses: Expense[], todayISO: string): number {
  if (!isValidISODate(todayISO)) return 0;
  const yearMonth = todayISO.slice(0, 7);

  let totalCents = 0;
  for (const e of expenses ?? []) {
    if (!e || e.status !== 'booked') continue;
    if (!isValidISODate(e.expenseDate)) continue;
    if (e.expenseDate.slice(0, 7) !== yearMonth) continue;
    totalCents += e.grossCents;
  }
  return totalCents;
}

// ─── §5 #10 — Aktivierungs-Checkliste (Loop-A/B-Hooks) ────────────────────────

/**
 * Stabile IDs der 5 Checklisten-Zeilen, feste Reihenfolge (Reihenfolge =
 * Reihenfolge des zurückgegebenen Arrays). `firstReceiptScanned` ist eine
 * CODER-ANNAHME für das 5. Item — architecture-ux-restructure.md §5 #10 nennt
 * textuell nur 4 Bedingungen (Profil/Onboarding, Empfangsadresse, ≥1 Rechnung,
 * StB eingeladen), das kanonische I/O-Paar verlangt aber exakt 5 Items in
 * fester Reihenfolge. Siehe changelog-ux-w4-cockpit-loaders.md — Architect
 * möge Wortlaut/Position in W4.2 bestätigen oder korrigieren.
 */
export type ChecklistItemId =
  | 'profileComplete'
  | 'inboundAddressActive'
  | 'firstInvoiceCreated'
  | 'advisorInvited'
  | 'firstReceiptScanned';

/** Eine Zeile der Aktivierungs-Checkliste. */
export interface ChecklistItem {
  id: ChecklistItemId;
  /** `true` ⇒ Zeile wird als ✓ erledigt gerendert. */
  done: boolean;
  /** Route (unter `/dashboard`, absoluter Pfad), auf die die Zeile deep-linkt. */
  deepLinkRoute: string;
}

/**
 * Inputs für {@link deriveChecklist}. Sichtbarkeits-Gating (z. B. `canInviteAdvisor`)
 * ist NICHT Teil dieser pure Funktion — sie liefert IMMER exakt 5 Items in fester
 * Reihenfolge; ob eine Zeile in der UI angezeigt wird, entscheidet die aufrufende
 * Seite (CockpitPage, M-UX-4.2) anhand der jeweiligen `canUseX`-Gates.
 */
export interface DeriveChecklistInputs {
  /**
   * `sellerData.onboardingCompleted === true` (`MainLayout.tsx:152`). Bleibt als
   * Input erhalten (Kontext/Telemetrie), ist aber seit M-UX-Cockpit-Home (§B)
   * NICHT mehr die done-Quelle für `profileComplete` — dafür dient das echte
   * Pflichtfeld-Prädikat {@link DeriveChecklistInputs.profileFieldsComplete}.
   */
  onboardingCompleted: boolean;
  /**
   * Echte Pflichtfeld-Prüfung (`hasSellerRequiredFields(sellerData)` —
   * `name` + vollständige Anschrift, `utils/sellerProfile.ts`). Ersetzt den
   * Wizard-Proxy `onboardingCompleted` als done-Quelle für `profileComplete`
   * (`plan-ux-table-cockpit-quality.md` §B: der Proxy zeigte den Schritt
   * fälschlich als erledigt, sobald der Wizard einmal durchgeklickt war).
   * Wird von der aufrufenden Seite (`CockpitPage`) berechnet und hier nur
   * als bereits-ermittelte Tatsache konsumiert (pure Funktion).
   */
  profileFieldsComplete: boolean;
  /** `inbound_mailboxes/{uid}` existiert (`InboxPage.tsx:172`). */
  inboundMailboxExists: boolean;
  /** Anzahl Rechnungen — aus Kachel-2-Daten (`loadIncomeForUser(...).length`). */
  invoiceCount: number;
  /** Mind. eine aktive/angenommene Steuerberater-Einladung. */
  advisorInvited: boolean;
  /** Anzahl gescannter Belege (`scanned_receipts`, unabhängig vom Status). */
  scannedReceiptCount: number;
}

/**
 * Pure Ableitung der Aktivierungs-Checkliste aus bereits geladenen Fakten.
 *
 * Kanonisches I/O-Paar (§5 / §B): alle Inputs `false`/`0` ⇒ 5 offene Items in
 * fester Reihenfolge; `profileFieldsComplete===true` ⇒ Item 1 (`profileComplete`)
 * ✓ (NICHT `onboardingCompleted` — der Wizard-Proxy ist entkoppelt, §B).
 */
export function deriveChecklist(inputs: DeriveChecklistInputs): ChecklistItem[] {
  return [
    {
      id: 'profileComplete',
      done: inputs.profileFieldsComplete === true,
      deepLinkRoute: '/dashboard/konto',
    },
    {
      id: 'inboundAddressActive',
      done: inputs.inboundMailboxExists === true,
      deepLinkRoute: '/dashboard/posteingang',
    },
    {
      id: 'firstInvoiceCreated',
      done: (inputs.invoiceCount ?? 0) >= 1,
      // Heutige Index-Route (Rechnungsformular) — vor W4.2s Index-Wechsel die
      // einzig sicher registrierte Route für "Rechnung erstellen".
      deepLinkRoute: '/dashboard',
    },
    {
      id: 'advisorInvited',
      done: inputs.advisorInvited === true,
      deepLinkRoute: '/dashboard/konto/steuerberater',
    },
    {
      id: 'firstReceiptScanned',
      done: (inputs.scannedReceiptCount ?? 0) >= 1,
      deepLinkRoute: '/dashboard/belege',
    },
  ];
}

// ─── §5 #4/#5/#6 — Arbeitsvorrat-Zähler (flip-ready: voll gebaut + getestet) ──

/** Ergebnis eines gedeckelten Zähl-Loaders — `capped` steuert die „N+"-Anzeige. */
export interface CockpitBoundedCount {
  /** Anzahl der im geladenen (ggf. gedeckelten) Fenster gezählten Items. */
  count: number;
  /** `true` ⇒ das Fenster war voll ausgeschöpft, die reale Zahl kann höher sein. */
  capped: boolean;
}

/** Größe des geladenen Fensters für den Posteingang-Zähler (Muster `InboxPage.tsx` `PAGE_SIZE`). */
const INBOUND_COUNT_WINDOW = 25;

/** Größe des geladenen Fensters für den Bank-Zähler (§5 #5: „limit 100", Anzeige „99+"). */
const BANK_COUNT_WINDOW = 100;

/**
 * Zählt "neue" `inbound_documents` (Posteingang, Loop A) im jüngsten Fenster
 * (bounded, Muster `InboxPage.tsx:142-152`, `PAGE_SIZE 25`).
 *
 * VEREINFACHUNG (Coder-Annahme, dokumentiert): „neu" = `processingStatus==='processed'`
 * innerhalb des geladenen Fensters. Das VOLLE Unified-Status-Modell (inkl.
 * Booked-Kreuzreferenz gegen `expenses.sourceReceivedInvoiceId`, §6.3) liefert
 * erst `utils/unifiedInbox/mappers.ts#deriveUnifiedStatus` (M-UX-5.1, paralleler
 * Batch-A-Task — bewusst NICHT importiert, um die Parallelisierbarkeit beider
 * Module nicht zu koppeln). Der Cockpit-Zähler kann dadurch kurzzeitig bereits
 * verbuchte Dokumente mitzählen, bis der Nutzer die Unified Inbox öffnet — siehe
 * changelog-ux-w4-cockpit-loaders.md.
 *
 * @param scopeUid - `effectiveScopeUid`. Aufrufer MUSS vorher `canUseInbound(scopeUid)` prüfen.
 */
export async function loadInboundNewCount(scopeUid: string): Promise<CockpitBoundedCount> {
  const q = query(
    collection(db, 'inbound_documents'),
    where('userId', '==', scopeUid),
    fsLimit(INBOUND_COUNT_WINDOW),
  );
  const snap = await getDocs(q);

  let count = 0;
  for (const d of snap.docs) {
    const data = d.data() as { processingStatus?: string };
    if (data.processingStatus === 'processed') count += 1;
  }

  return { count, capped: snap.docs.length === INBOUND_COUNT_WINDOW };
}

/**
 * Zählt unzugeordnete Bank-Transaktionen (`matchStatus==='unmatched'`) im
 * ersten Fenster (limit 100, Anzeige „99+"-Cap, Muster
 * `components/bank/loadReconcileData.ts:72-83`).
 *
 * BEKANNTE EINSCHRÄNKUNG: `listTransactions` (api/bankApi.ts) liest intern über
 * `requireCurrentUser()`, NICHT über den übergebenen `scopeUid` — im
 * Mandanten-Modus (Advisor, `effectiveScopeUid !== currentUser.uid`) liefert
 * dieser Loader daher die Bank-Daten des EIGENEN Tenants, nicht die des
 * Mandanten. Das ist eine Vorbestehende Einschränkung von `bankApi.ts`
 * (nicht Teil dieses Deltas) — dokumentiert in changelog-ux-w4-cockpit-loaders.md
 * für W4.2/Advisor-Wellen.
 *
 * @param scopeUid - `effectiveScopeUid`. Aufrufer MUSS vorher `canUseBankReconcile(uid, tier)` prüfen.
 */
export async function loadBankUnmatchedCount(scopeUid: string): Promise<CockpitBoundedCount> {
  void scopeUid; // s. JSDoc „Bekannte Einschränkung" — listTransactions ignoriert dieses Argument.
  const result = await listTransactions({ matchStatus: 'unmatched', limit: BANK_COUNT_WINDOW });
  return { count: result.txs.length, capped: result.nextCursor !== null };
}

/**
 * Zählt gescannte Belege mit `status==='extracted'` (warten auf Buchung).
 * `getScannedReceiptsForUser` lädt unbounded (kein Limit) — `capped` ist daher
 * immer `false`.
 *
 * @param scopeUid - `effectiveScopeUid`. Aufrufer MUSS vorher
 *   `canUseReceiptScan(uid, tier)` prüfen.
 */
export async function loadScannedExtractedCount(scopeUid: string): Promise<CockpitBoundedCount> {
  const receipts = await getScannedReceiptsForUser(scopeUid);
  const count = receipts.filter((r) => r.status === 'extracted').length;
  return { count, capped: false };
}
